<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import type {
  IYaCaptchaEmits,
  IYaCaptchaExpose,
  IYaCaptchaProps,
} from "./VYaCaptcha.types";
import loadScript from "../../utilities/loadScript";
import { waitForThirdParty } from "@magnit/core/src/utilities/async";
import { CAPTCHA_SCRIPT_URL } from "../../constants/captcha";

const props = withDefaults(defineProps<IYaCaptchaProps>(), {
  invisible: false,
  hideShield: false,
  noAutoExecute: false,
});

const rootClass = computed(() => ({
  captcha: true,
  captcha_visible: !props.invisible,
}));

const CAPTCHA_FIELD = "smartCaptcha";
// Смарткапча это внутреняя кухня конкретно этого компонента!
// Её типизация не должна торчать глобально на окно, другим компонентам не зачем знать про это.
// Компоненты из вне если нужно взаимодействуют с этим через экспознутые методы.
type SmartCaptcha = {
  destroy: (widgetId?: string) => void;
  getResponse: (widgetId?: string) => string | undefined;
  subscribe: (
    widgetId: string,
    event:
      | "challenge-visible"
      | "challenge-hidden"
      | "network-error"
      | "success",
    callback: (token?: string) => void,
  ) => () => void;
  render: (
    container: HTMLElement | string,
    params: {
      sitekey: string;
      callback?: (token: string) => void;
      hl?: "ru" | "en" | "be" | "kk" | "tt" | "uk" | "uz" | "tr";
      invisible?: boolean;
      hideShield?: boolean;
    },
  ) => string;
  reset: (widgetId?: string) => void;
  execute: (widgetId?: string) => void;
};

const getSmartCaptcha = (): SmartCaptcha | null => {
  if (typeof window === "undefined") {
    return null;
  }

  return CAPTCHA_FIELD in window ? (window.smartCaptcha as SmartCaptcha) : null;
};

const element = ref<HTMLDivElement | null>(null);
const widgetId = ref("");
const emit = defineEmits<IYaCaptchaEmits>();

const destroy = () => {
  const smartCaptcha = getSmartCaptcha();
  if (!smartCaptcha) {
    return;
  }

  smartCaptcha.destroy(widgetId.value);
};

const getResponse = () => {
  const smartCaptcha = getSmartCaptcha();
  if (!smartCaptcha) {
    return;
  }

  return smartCaptcha.getResponse(widgetId.value);
};

const execute = () => {
  const smartCaptcha = getSmartCaptcha();
  if (!smartCaptcha) {
    return;
  }
  // этот метод вызывает проверку невидимой капчи и генерирует токен
  // либо если пользователь подозрительный, то вызывает отображение капча испытания
  smartCaptcha.execute(widgetId.value);
};

const reset = () => {
  const smartCaptcha = getSmartCaptcha();
  if (!smartCaptcha) {
    return;
  }

  emit("tokenChanged", "");
  smartCaptcha.reset(widgetId.value);
};

const init = () => {
  const smartCaptcha = getSmartCaptcha();
  if (!smartCaptcha) {
    return;
  }

  if (element.value) {
    const captchaWidgetId = smartCaptcha.render(element.value, {
      sitekey: props.captchaKey,
      hl: "ru",
      invisible: props.invisible,
      hideShield: props.hideShield,
    });
    widgetId.value = captchaWidgetId;
    smartCaptcha.subscribe(captchaWidgetId, "success", (token) =>
      emit("tokenChanged", token ?? ""),
    );
    if (props.invisible && !props.noAutoExecute) {
      execute();
    }
  }
};

let loadPromise: Promise<void> | null = null;

// Возможно вообще объединить с методом init? Только важно избегать двойной инициализации ошибочной
const load = async () => {
  if (!loadPromise) {
    loadPromise = loadScript(CAPTCHA_SCRIPT_URL, {
      // капче исторически у нас был прописан defer, видимо скрипт капчи где-то ожидает уже построенный DOM
      // ну я решил оставить как дань традиции даже после рефакторинга))
      defer: true,
    })
      .then(() => waitForThirdParty(CAPTCHA_FIELD))
      .catch((error) => {
        console.error(error);
        loadPromise = null;
      });
  }
  return loadPromise;
};

onMounted(async () => {
  // подчистить токен при ре-маунте, чтобы внешний использующий компонент точно получил синхронное состояние с фактическим состоянием капчи сейчас
  emit("tokenChanged", "");
  await load();
  init();
});

defineExpose<IYaCaptchaExpose>({
  destroy,
  init,
  getResponse,
  reset,
  execute,
  load,
});
</script>

<template>
  <div ref="element" :class="rootClass" />
</template>

<style scoped lang="postcss">
.captcha {
  width: 100%;

  &_visible {
    height: 102px;
  }
}
</style>
